import { Stack, Typography, Box } from "@mui/material";
import { Campaign } from "../../types/apiTypes";
import { Link, useNavigate } from "react-router-dom";
import {
  AppRoutes,
  ButtonsColorType,
  ButtonsType,
  CampaignPurpose,
} from "../../types/enums";
import { formatDateRange } from "../../utils/formatDateRange";
import { RolesIconsBlock } from "./RolesIconsBlock";
import { NewCampaignBlock } from "./NewCampaignBlock";
import { ReuseButton, ReuseTooltip } from "../ui";
import { ProductAnalyticsBlock } from "./ProductAnalyticsBlock";
import { GapAnalysisBlock } from "./GapAnalysisBlock";
import { AccordionArrowDown } from "../images/AccordionArrowDown";
import { useState } from "react";

export const ProductListItem = ({ product }: { product: Campaign }) => {
  const [isGapAnalysisOpen, setIsGapAnalysisOpen] = useState<boolean>(false);

  const {
    id,
    product_name,
    company_name,
    total_accounts,
    last_updated_at,
    user_roles,
    invited_users_count,
    is_new,
    campaign_purpose,
    analytics,
    statistics,
    cids,
  } = product;
  const navigate = useNavigate();

  const isDemo = [CampaignPurpose.demo, CampaignPurpose.sales].includes(
    campaign_purpose,
  );

  return (
    <Stack
      sx={{
        gap: "12px",
        border: isDemo ? "none" : "1px solid #D9D9D9",
        padding: "16px",
        borderRadius: "8px",
        position: "relative",
      }}
    >
      <Stack
        sx={{
          width: "100%",
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <Stack gap="16px" direction="row" alignItems="center">
          <Stack gap="8px" direction="row" color="custom" alignItems="center">
            <Link
              to={`${AppRoutes.CAMPAIGNS}/${id}`}
              style={{ textDecoration: "none" }}
            >
              <ReuseButton
                buttonType={ButtonsType.text}
                sx={{
                  ":hover": {
                    backgroundColor: "#0000000D",
                  },
                  fontSize: "16px",
                }}
              >
                {product_name}
              </ReuseButton>
            </Link>

            <Box
              sx={{
                width: "4px",
                height: "4px",
                borderRadius: "50%",
                backgroundColor: "#A9A9A9",
              }}
            />

            <Typography variant="subtitle1" color="#A9A9A9" fontWeight={500}>
              {company_name}
            </Typography>
          </Stack>
          <RolesIconsBlock
            userRoles={user_roles || []}
            isShared={invited_users_count > 1}
          />
          {is_new && <NewCampaignBlock />}
        </Stack>

        <Stack flexDirection={"row"} gap={"16px"} alignItems={"center"}>
          {cids?.length > 0 && (
            <>
              <Typography
                variant="body1"
                color="custom.textDarkBlue"
                fontWeight={500}
                sx={{ display: "flex", gap: "4px", alignItems: "center" }}
              >
                CID:
                <ReuseTooltip
                  maxWidth={"232px"}
                  hideChildren={false}
                  text={
                    cids.length > 1 ? (
                      <Stack sx={{ fontSize: "12px", lineHeight: "18px" }}>
                        <Typography>Connected CID:</Typography>
                        <Typography>
                          {cids.reduce(
                            (acc, el, idx) =>
                              acc +
                              ` ${el}${idx === cids.length - 1 ? "." : ","}`,
                            "",
                          )}
                        </Typography>
                      </Stack>
                    ) : undefined
                  }
                  style={{ fontWeight: 400 }}
                >
                  {`${cids[0]} ${cids.length > 1 ? "..." : ""}`}
                </ReuseTooltip>
              </Typography>
              <Box
                sx={{
                  width: "4px",
                  height: "4px",
                  borderRadius: "50%",
                  backgroundColor: "#A9A9A9",
                }}
              />
            </>
          )}

          <Typography
            variant="body1"
            color="custom.textDarkBlue"
            fontWeight={500}
            sx={{ display: "flex", gap: "4px", alignItems: "center" }}
          >
            Last update:
            <Typography component="span">
              {formatDateRange(last_updated_at, true)}
            </Typography>
          </Typography>
        </Stack>
      </Stack>

      <ProductAnalyticsBlock
        analytics={analytics}
        statistics={statistics}
        total_accounts={total_accounts}
      />

      {isGapAnalysisOpen && (
        <GapAnalysisBlock
          role_gaps={analytics.role_gaps}
          title_gaps={analytics.title_gaps}
        />
      )}

      <Stack direction="row" justifyContent="space-between">
        <Stack
          flexDirection={"row"}
          gap={"6px"}
          alignItems={"center"}
          sx={{ cursor: "pointer" }}
          onClick={() => setIsGapAnalysisOpen((prev) => !prev)}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              transform: isGapAnalysisOpen ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.3s ease",
            }}
          >
            <AccordionArrowDown color={"#1C3C6C"} opacity={"1"} />
          </Box>

          <Typography
            sx={{
              fontSize: "16px",
              lineHeight: "24px",
              fontWeight: 500,
              color: "#1C3C6C",
            }}
          >
            Gap Analysis
          </Typography>
        </Stack>
        <Stack direction="row" gap="8px">
          <ReuseButton onClick={() => navigate(`${AppRoutes.CAMPAIGNS}/${id}`)}>
            Campaign Details
          </ReuseButton>
          <ReuseButton
            buttonType={ButtonsType.contained}
            buttonColor={ButtonsColorType.blue_white}
            onClick={() => navigate(`${AppRoutes.ACCOUNTS_LIST}/${id}`)}
          >
            View Accounts
          </ReuseButton>
        </Stack>
      </Stack>
    </Stack>
  );
};
